import { useState, useEffect } from 'react';
import { InputOTP, Button, Input } from '@airtm/airtm-ui';
import { Trans, useTranslation } from 'react-i18next';
import { useToast } from 'utils/hooks/useToast';
import cx from 'classnames';

export const RESEND_TIMEOUT = 30;

export default function EmailCodeVerification({
  onSubmit,
  onResend,
  autoSubmit = true,
  active = true,
  className,
}: Readonly<{
  onSubmit: ({ verificationCode }: { verificationCode: string }) => Promise<void>;
  onResend: () => Promise<void>;
  autoSubmit?: boolean;
  active?: boolean;
  className?: string;
}>) {
  const [code, setCode] = useState('');
  const [hasResentEmail, setHasResentEmail] = useState(false);

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['SIGNUP', 'SETTINGS', 'LOGIN']);
  const [countdown, setCountdown] = useState(RESEND_TIMEOUT);
  const { toastErr } = useToast();

  useEffect(() => {
    if (!active || countdown === 0) return;

    const interval = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [countdown, active]);

  function resetState() {
    setCode('');
    setError(false);
    setLoading(false);
    setCountdown(RESEND_TIMEOUT);
  }

  useEffect(() => {
    if (!active) {
      resetState();
    }
  }, [active]);

  async function handleSubmit(verificationCode?: string) {
    setLoading(true);
    try {
      await onSubmit({ verificationCode: verificationCode || code });
    } catch {
      setError(true);
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  const onCodeChange = async (value: string) => {
    setCode(value);
    if (value.length === 6 && autoSubmit) {
      await handleSubmit(value);
    } else {
      setError(false);
    }
  };

  async function resendHandler() {
    try {
      await onResend();
      setHasResentEmail(true);
      setCountdown(RESEND_TIMEOUT);
    } catch (err) {
      toastErr(err);
    }
  }

  return (
    <>
      <InputOTP
        autoComplete="off"
        autoFocus
        id="email-verification-code"
        name="email-verification-code"
        label={t('SIGNUP:CHECK_CODE_SENT_MESSAGE')}
        onChange={onCodeChange}
        placeholder={t('SETTINGS:CODE_PLACEHOLDER')}
        size="lg"
        type="text"
        value={code}
        maxLength={6}
        invalid={error}
      />
      {error && <Input.HelperText invalid>{t('SIGNUP:INVALID_EMAIL_CODE')}</Input.HelperText>}

      {!autoSubmit && (
        <Button
          variant="primary"
          onClick={() => handleSubmit()}
          disabled={code.length < 6}
          className="w-100 u-mt-3"
          loading={loading}
        >
          {t('SETTINGS:SECONDARY_EMAILS_BUTTON')}
        </Button>
      )}

      <div className={cx('text--sm', className)}>
        <div className="d-flex flex-column">
          {(countdown === 0 || hasResentEmail) && (
            <p>
              <Trans
                i18nKey={
                  hasResentEmail && countdown > 0
                    ? 'SIGNUP:VERIFY_EMAIL_CODE_SENT_AGAIN'
                    : 'LOGIN:MAIL_LANDING_TEXT_2'
                }
                components={[
                  <Button
                    key="resend-email-button"
                    variant="link"
                    className="link text--underline text--sm pb-1"
                    size="small"
                    disabled={countdown > 0}
                    onClick={() => {
                      resendHandler();
                    }}
                  />,
                ]}
              />
            </p>
          )}
          {countdown > 0 && (
            <p>
              {t(`SIGNUP:EMAIL_VERIFICATION_RESEND_COUNTDOWN`, {
                timer: countdown,
              })}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
